/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { dashboardAPI, ticketsAPI } from '../../lib/api';
import { formatDate, formatRelativeTime, TICKET_STATUS, TICKET_TYPES, TICKET_PRIORITY } from '../../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  FileText, Calendar, Clock, CheckCircle, AlertCircle,
  ArrowRight, Megaphone, Plus, MapPin, Home, Eye,
  Loader2, TrendingUp, BarChart3, ChevronRight, ClipboardList, Lightbulb,
} from 'lucide-react';

const STATUS_COLORS = {
  yeni: '#6366f1', inceleniyor: '#3b82f6', yonlendirildi: '#0ea5e9',
  islemde: '#f59e0b', yanit_bekleniyor: '#f97316', cozuldu: '#10b981',
  kapatildi: '#64748b', beklemede: '#eab308', iptal: '#ef4444',
};

const StatCard = ({ title, value, icon: Icon, color = 'emerald', sub }) => {
  const cls = {
    emerald: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
    blue: 'bg-blue-50 text-blue-600 ring-blue-100',
    amber: 'bg-amber-50 text-amber-600 ring-amber-100',
    violet: 'bg-violet-50 text-violet-600 ring-violet-100',
  };
  return (
    <Card className="group hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid={`stat-${title.replace(/\s/g, '-').toLowerCase()}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-extrabold text-slate-900 mt-1 tracking-tight">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-1.5">{sub}</p>}
          </div>
          <div className={`p-3 rounded-xl ring-1 ${cls[color]} group-hover:scale-110 transition-transform duration-300`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ProgressBar = ({ label, value, total, color }) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="w-36 text-sm text-slate-600 truncate">{label}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
        <div
          className="h-full rounded-full flex items-center justify-end pr-2 text-[11px] font-semibold text-white transition-all duration-700 ease-out"
          style={{ width: `${Math.max(pct, pct > 0 ? 12 : 0)}%`, backgroundColor: color }}
        >
          {value}
        </div>
      </div>
      <span className="text-xs text-slate-400 w-10 text-right">%{pct.toFixed(0)}</span>
    </div>
  );
};

const MuhtarDashboard = () => {
  const { user } = useAuth();
  usePageTitle('Muhtar Paneli');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [mahalleReport, setMahalleReport] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentAnnouncements, setRecentAnnouncements] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);

  useEffect(() => { loadData(); }, []);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, ticketsRes, announcementsRes, meetingsRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getRecentTickets(10),
        dashboardAPI.getRecentAnnouncements(5),
        dashboardAPI.getUpcomingMeetings(5),
      ]);
      setStats(statsRes.data);
      setRecentTickets(ticketsRes.data);
      setRecentAnnouncements(announcementsRes.data);
      setUpcomingMeetings(meetingsRes.data);
      try {
        const reportRes = await dashboardAPI.getMuhtarMahalleReport();
        setMahalleReport(reportRes.data);
      } catch { /* permission may not be set */ }
    } catch (err) {
      console.error('Dashboard veri yuklenemedi:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const resolutionRate = stats
    ? (stats.total_tickets > 0 ? Math.round((stats.resolved_tickets / stats.total_tickets) * 100) : 0)
    : 0;

  const getStatusBadge = (status) => {
    const s = TICKET_STATUS[status] || { label: status, color: 'default' };
    const cls = { success: 'bg-emerald-50 text-emerald-700', warning: 'bg-amber-50 text-amber-700', error: 'bg-red-50 text-red-700', info: 'bg-blue-50 text-blue-700', default: 'bg-slate-100 text-slate-700' };
    return <Badge className={cls[s.color] || cls.default}>{s.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="muhtar-dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="muhtar-dashboard">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 p-6 text-white" data-testid="muhtar-welcome-banner">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/15 backdrop-blur-sm rounded-xl">
              <Home className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Muhtar Paneli</h1>
              <p className="text-emerald-100 mt-0.5">Hos geldiniz, {user?.name} {user?.surname}</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 text-emerald-100 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">Mahallenize ait bilgileri buradan takip edebilirsiniz</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="muhtar-stats-grid">
        <StatCard title="Mahalle Talepleri" value={stats?.total_tickets || 0} icon={FileText} color="blue" />
        <StatCard title="Bekleyen Talepler" value={stats?.open_tickets || 0} icon={Clock} color="amber" sub={stats?.open_tickets > 0 ? 'Islem bekliyor' : null} />
        <StatCard title="Cozulen Talepler" value={stats?.resolved_tickets || 0} icon={CheckCircle} color="emerald" />
        <StatCard title="Cozum Orani" value={`%${resolutionRate}`} icon={TrendingUp} color="violet" />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="muhtar-quick-actions">
        <Card className="group hover:shadow-md transition-all duration-300 border-emerald-200/50 bg-gradient-to-br from-emerald-50/50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Mahalle Duyurusu</h3>
                <p className="text-slate-500 text-sm mt-0.5">Mahallenize ozel duyuru yayinlayin</p>
              </div>
              <Link to="/panel/duyurular/yeni">
                <Button className="bg-emerald-600 hover:bg-emerald-700 shadow-sm" data-testid="create-announcement-btn">
                  <Megaphone className="w-4 h-4 mr-2" /> Duyuru Yayinla
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
        <Card className="group hover:shadow-md transition-all duration-300 border-blue-200/50 bg-gradient-to-br from-blue-50/50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Yeni Talep</h3>
                <p className="text-slate-500 text-sm mt-0.5">Mahalle adina talep olusturun</p>
              </div>
              <Link to="/panel/talepler/yeni">
                <Button className="bg-blue-600 hover:bg-blue-700 shadow-sm" data-testid="create-ticket-btn">
                  <Plus className="w-4 h-4 mr-2" /> Talep Olustur
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
        <Card className="group hover:shadow-md transition-all duration-300 border-violet-200/50 bg-gradient-to-br from-violet-50/50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Proje Onerisi</h3>
                <p className="text-slate-500 text-sm mt-0.5">Mahalle icin proje onerin</p>
              </div>
              <Link to="/panel/talepler/proje-onerisi">
                <Button className="bg-amber-600 hover:bg-amber-700 shadow-sm" data-testid="create-project-btn">
                  <Lightbulb className="w-4 h-4 mr-2" /> Proje Oner
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
        <Card className="group hover:shadow-md transition-all duration-300 border-violet-200/50 bg-gradient-to-br from-violet-50/50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Anketler</h3>
                <p className="text-slate-500 text-sm mt-0.5">Aktif anketlere katilim saglayin</p>
              </div>
              <Link to="/panel/anketler">
                <Button className="bg-violet-600 hover:bg-violet-700 shadow-sm" data-testid="surveys-btn">
                  <ClipboardList className="w-4 h-4 mr-2" /> Anketler
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts + Meetings/Announcements - Two Column */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Ticket Distribution */}
        <div className="space-y-6">
          {/* Ticket Status Chart */}
          {mahalleReport && Object.keys(mahalleReport.tickets_by_status || {}).length > 0 && (
            <Card className="hover:shadow-md transition-shadow duration-300" data-testid="ticket-status-chart">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-600" />
                  <CardTitle className="text-base">Talep Durum Dagilimi</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(mahalleReport.tickets_by_status).map(([status, count]) => (
                    <ProgressBar
                      key={status}
                      label={TICKET_STATUS[status]?.label || status}
                      value={count}
                      total={mahalleReport.total_tickets}
                      color={STATUS_COLORS[status] || '#94a3b8'}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Category Distribution */}
          {mahalleReport && Object.keys(mahalleReport.tickets_by_category || {}).length > 0 && (
            <Card className="hover:shadow-md transition-shadow duration-300" data-testid="ticket-category-chart">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <CardTitle className="text-base">Kategori Dagilimi</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(mahalleReport.tickets_by_category).map(([cat, count]) => (
                    <div key={cat} className="bg-slate-50 rounded-xl p-4 text-center hover:bg-slate-100 transition-colors">
                      <p className="text-2xl font-bold text-slate-900">{count}</p>
                      <p className="text-xs text-slate-500 capitalize mt-1">{cat}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Type Distribution */}
          {mahalleReport && Object.keys(mahalleReport.tickets_by_type || {}).length > 0 && (
            <Card className="hover:shadow-md transition-shadow duration-300" data-testid="ticket-type-chart">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-violet-600" />
                  <CardTitle className="text-base">Talep Turu Dagilimi</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(mahalleReport.tickets_by_type).map(([type, count]) => {
                    const info = TICKET_TYPES[type] || { label: type };
                    return (
                      <div key={type} className="bg-slate-50 rounded-xl p-4 text-center hover:bg-slate-100 transition-colors">
                        <p className="text-2xl font-bold text-slate-900">{count}</p>
                        <p className="text-xs text-slate-500 mt-1">{info.label}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Meetings + Announcements */}
        <div className="space-y-6">
          {/* Upcoming Meetings */}
          <Card className="hover:shadow-md transition-shadow duration-300" data-testid="upcoming-meetings">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-600" />
                  <CardTitle className="text-base">Yaklasan Toplantilar</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {upcomingMeetings.length > 0 ? (
                <div className="space-y-1">
                  {upcomingMeetings.map((m) => (
                    <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`meeting-${m.id}`}>
                      <div className="w-12 h-12 rounded-xl bg-emerald-50 flex flex-col items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-emerald-700 leading-none">
                          {new Date(m.date).toLocaleDateString('tr-TR', { day: '2-digit' })}
                        </span>
                        <span className="text-[10px] text-emerald-500 uppercase leading-none mt-0.5">
                          {new Date(m.date).toLocaleDateString('tr-TR', { month: 'short' })}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm text-slate-900 truncate">{m.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-500">{m.time}</span>
                          {m.location && <span className="text-xs text-slate-400 truncate">- {m.location}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Calendar className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Yaklasan toplanti bulunmuyor</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Announcements */}
          <Card className="hover:shadow-md transition-shadow duration-300" data-testid="recent-announcements">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-amber-600" />
                  <CardTitle className="text-base">Son Duyurular</CardTitle>
                </div>
                <Link to="/panel/duyurular">
                  <Button variant="ghost" size="sm" className="text-xs text-slate-500 hover:text-slate-700" data-testid="see-all-announcements-btn">
                    Tumu <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {recentAnnouncements.length > 0 ? (
                <div className="space-y-1">
                  {recentAnnouncements.map((a) => (
                    <Link
                      key={a.id}
                      to={`/panel/duyurular/${a.id}`}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                      data-testid={`announcement-${a.id}`}
                    >
                      <div className="p-2 rounded-lg bg-amber-50 shrink-0 group-hover:bg-amber-100 transition-colors">
                        <Megaphone className="w-4 h-4 text-amber-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{a.title}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{formatRelativeTime(a.created_at)}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Megaphone className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Henuz duyuru bulunmuyor</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Muhtar Info Card */}
          <Card className="hover:shadow-md transition-shadow duration-300 border-emerald-100" data-testid="muhtar-info-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Muhtar Olarak Yapabilecekleriniz</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { icon: Eye, color: 'blue', title: 'Talepleri Goruntuleme', desc: 'Mahallenize ait tum talepleri goruntuleyebilirsiniz' },
                  { icon: Plus, color: 'emerald', title: 'Talep Olusturma', desc: 'Mahalle adina talep olusturabilirsiniz' },
                  { icon: Megaphone, color: 'amber', title: 'Duyuru Yayinlama', desc: 'Mahallenize ozel duyuru yayinlayabilirsiniz' },
                ].map((item, i) => (
                  <div key={item.title} className="flex items-start gap-3 p-3 bg-slate-50/70 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className={`p-2 rounded-lg bg-${item.color}-100 shrink-0`}>
                      <item.icon className={`w-4 h-4 text-${item.color}-600`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-slate-900">{item.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent Tickets Table */}
      <Card className="hover:shadow-md transition-shadow duration-300" data-testid="recent-tickets-table">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-emerald-600" />
              Mahalle Talepleri
            </CardTitle>
            <CardDescription className="mt-0.5">Mahallenize ait son talepler</CardDescription>
          </div>
          <Link to="/panel/talepler">
            <Button variant="outline" size="sm" data-testid="see-all-tickets-btn">
              Tumunu Gor <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentTickets.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    <TableHead className="text-xs">Talep No</TableHead>
                    <TableHead className="text-xs">Konu</TableHead>
                    <TableHead className="text-xs">Tur</TableHead>
                    <TableHead className="text-xs">Durum</TableHead>
                    <TableHead className="text-xs">Tarih</TableHead>
                    <TableHead className="text-xs text-right">Islem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentTickets.slice(0, 7).map((ticket) => (
                    <TableRow key={ticket.id} className="hover:bg-slate-50/50" data-testid={`ticket-row-${ticket.id}`}>
                      <TableCell className="font-mono text-xs text-blue-600 font-medium">{ticket.ticket_no}</TableCell>
                      <TableCell className="max-w-[250px]">
                        <p className="text-sm font-medium text-slate-900 truncate">{ticket.subject}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {TICKET_TYPES[ticket.type]?.label || ticket.type}
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{formatRelativeTime(ticket.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Link to={`/panel/talepler/${ticket.id}`}>
                          <Button variant="ghost" size="sm" className="h-8" data-testid={`view-ticket-${ticket.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-10">
              <FileText className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400">Mahallenizde henuz talep bulunmuyor</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MuhtarDashboard;
